import { Entity } from "../../helpers/Entity"
import { INTEGRATION_KEY } from "../../integration/Constants"
import ExternalLink, { ExternalLinkDto } from "../../integration/ExternalLink"
import ContactAddressFormatter from "../AddressFormatter"

export type ContactAttributesDto = {
    name: string
    address: ContactAddressDto
    organizationName: string
    organizationId: string
    phoneNumber?:string[]
    phoneNumbers: ContactPhoneNumberDto[]
    email: string
    firstName: string
    lastName: string
    notes: string
    paymentTermId: string
    taxId: string
    taxNumber: string
    title: string
    language: string
    labels: ContactLabelDto[]
    persons: Contact[] //if contact is organization  (split person / org)
    organization: Organization
    primaryContactId: string
    primaryContactName: string
    deletedAt: string
    labelIds: string[]
    customerAccountIds : string[]
    createdAt:string
    creditLimit: number
    externalLinks: ExternalLinkDto[]
}

export type ContactFormAttributes={
    duplicatedContactsByEmail: ContactDto[]
    duplicatedContactsByPhoneNumber:ContactDto[]
}

export type ContactLabelDto = {
    type: string
    id: string
    attributes: {
        type: string
    }
}

export type ContactPhoneNumberDto = {
    type: string
    phoneNumber: string
    extension: string
}

export type ContactAddressDto = {
    id: string
    addressType: string
    city: string
    country: string
    countryCode: string
    firstName: string
    isDefault: boolean
    lastName: string
    postalCode: string
    state: string
    stateCode: string
    street: string
    street2: string
}

export type Organization = {
    attributes: OrganizationAttributes
}

type OrganizationAttributes = {
    name: string
    address: ContactAddressDto
}

export type ContactDto = {
    id: string
    type: string
    attributes: ContactAttributesDto
}

export default class Contact implements Entity {
    private dto: ContactDto
    private address: ContactAddressFormatter
    private externalLinks: ExternalLink[]

    constructor(dto: ContactDto) {
        this.dto = dto
        this.address = new ContactAddressFormatter(dto.attributes.address)
        this.externalLinks = this.dto.attributes.externalLinks.map(l => new ExternalLink(l))
    }

    isLoaded(): boolean {
        return (this.getId() != "" && this.dto.type == "persons") || this.dto.type == "organizations"
    }

    getId(): string {
        return this.dto.id
    }

    getCountryCode(): string {
        return this.dto.attributes.address.countryCode
    }

    async getFormattedAddress(): Promise<string[]> {
        return await this.address.getFormattedAddressValues()
    }

    getDto(): ContactDto{
        return this.dto
    }

    getType(): string {
        return this.dto.type
    }

    getPrimaryContactId(): string {
        return this.dto.attributes.primaryContactId
    }

    getTitle() {
        return this.dto.attributes.title
    }

    getFirstName() {
        return this.dto.attributes.firstName
    }

    getLastName() {
        return this.dto.attributes.lastName
    }

    getEmail() {
        return this.dto.attributes.email
    }

    getCompanyId() {
        return this.dto.attributes.organizationId
    }

    getCompanyName() {
        return this.dto.attributes.organizationName
    }

    getAddressId() {
        return this.dto.attributes.address.id
    }

    getAddress() {
        return this.dto.attributes.address.street
    }

    getAddress2() {
        return this.dto.attributes.address.street2
    }

    getCountry() {
        return this.dto.attributes.address.country
    }

    getState() {
        return this.dto.attributes.address.state
    }

    getStateCode() {
        return this.dto.attributes.address.stateCode
    }

    getCity() {
        return this.dto.attributes.address.city
    }

    getPostalCode() {
        return this.dto.attributes.address.postalCode
    }

    getPrimaryPhoneNumber() {
        for (let i = 0; i < this.dto.attributes.phoneNumbers.length; i++) {
            if (this.dto.attributes.phoneNumbers[i]["type"] == "primary") {
                return this.dto.attributes.phoneNumbers[i]["phoneNumber"]
            }
        }
        return ""
    }

    getPrimaryPhoneNumberExtension() {
        for (let i = 0; i < this.dto.attributes.phoneNumbers.length; i++) {
            if (this.dto.attributes.phoneNumbers[i].type == "primary") {
                return this.dto.attributes.phoneNumbers[i]["extension"]
            }
        }
        return ""
    }

    getMobilePhoneNumber() {
        for (let i = 0; i < this.dto.attributes.phoneNumbers.length; i++) {
            if (this.dto.attributes.phoneNumbers[i].type == "mobile") {
                return this.dto.attributes.phoneNumbers[i]["phoneNumber"]
            }
        }
        return ""
    }

    getContactLanguage() {
        return this.dto.attributes.language
    }

    getCustomerTypeId() {
        return this.dto.attributes.labels.filter(l => l.attributes.type === "customer-type").map(l => l.id)[0] || ""
    }

    getPaymentTermId() {
        return this.dto.attributes.paymentTermId
    }

    getTaxId() {
        return this.dto.attributes.taxId
    }

    getTaxNumber() {
        return this.dto.attributes.taxNumber
    }

    getNotes() {
        return this.dto.attributes.notes
    }

    getEmployees() {
        return this.dto.attributes.persons
    }

    getAllAttributes() {
        return this.dto.attributes
    }

    getName(): string {
        return this.dto.attributes.name
    }

    getCreditLimit(): number {
        return this.dto.attributes.creditLimit
    }

    getExternalIDByIntegrationKey(integrationKey: INTEGRATION_KEY): string | undefined {
        const link = this.externalLinks.find(l => l.getIntegrationKey() == integrationKey)
        if (link) {
            return link.getExternalId()
        }

        return
    }

    getCustomerAccountIds(): string[] {
        return this.dto.attributes.customerAccountIds
    }
}




// WEBPACK FOOTER //
// ./src/state/contact/models/contact.ts